import { hexStringToRgbString, numberToHexString, uint24ToRgbString } from './color-utils'

export interface ShaderColor {
    rgbString: string
    hexString: string
}

const getColorClosure = (colors: ShaderColor[]): (() => ShaderColor) => {
    let i = 0
    return (): ShaderColor => colors[i++ % colors.length]
}

const _SHADER_COLORS = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff]
export const SHADER_COLORS: ShaderColor[] = _SHADER_COLORS.map(color => ({
    rgbString: uint24ToRgbString(color),
    hexString: numberToHexString(color),
}))

const getMarkerColor = getColorClosure(SHADER_COLORS)
export const getAnnotationColor = getColorClosure(SHADER_COLORS)

export const SHADER_CONTROL_KEY_OPACITY = 'opacity'
export const SHADER_CONTROL_KEY_COLOR = 'color'
export const SHADER_CONTROL_KEY_BRIGHTNESS = 'brightness'
export const SHADER_CONTROL_KEY_CONTRAST = 'contrast'
export const SHADER_CONTROL_KEY_NORMALIZE = 'normalized'

export const createMarkerShader = (color: string | null): string => `
// docs: https://github.com/google/neuroglancer/blob/master/src/neuroglancer/sliceview/image_layer_rendering.md
#uicontrol vec3 ${SHADER_CONTROL_KEY_COLOR} color(default="${
    color ? hexStringToRgbString(color) : getMarkerColor().rgbString
}")
#uicontrol invlerp normalized
void main() {
  emitRGB(${SHADER_CONTROL_KEY_COLOR} * normalized());
}
`

export const createRGBShader = (): string => `
// docs: https://github.com/google/neuroglancer/blob/master/src/neuroglancer/sliceview/image_layer_rendering.md
#uicontrol invlerp normalized\n        void main() {
    emitRGB(vec3(toNormalized(getDataValue(0)),
    toNormalized(getDataValue(1)),
    toNormalized(getDataValue(2))));
}
`

export const createSelectionsShader = (): string => `
// docs: https://github.com/google/neuroglancer/blob/master/src/neuroglancer/annotation/rendering.md
void main() {
    vec4 color = prop_annotation_color();
    setPointMarkerColor(color);
    setPointMarkerBorderColor(color);
    setPointMarkerSize(8.);
    setPointMarkerBorderWidth(.0);
}
`

export const createAnnotationShader = (
    opacity: number | null | undefined,
    color: string | null | undefined
): string => `
// docs: https://github.com/google/neuroglancer/blob/master/src/neuroglancer/annotation/rendering.md
#uicontrol float ${SHADER_CONTROL_KEY_OPACITY} slider(min=0, max=1, default=${opacity ?? 1})
#uicontrol vec3 ${SHADER_CONTROL_KEY_COLOR} color(default="${
    color ? hexStringToRgbString(color) : getAnnotationColor().rgbString
}")
void main() {
    setPointMarkerColor(vec4(${SHADER_CONTROL_KEY_COLOR}, ${SHADER_CONTROL_KEY_OPACITY}));
    setPointMarkerBorderColor(vec4(0, 0, 0, ${SHADER_CONTROL_KEY_OPACITY}));
    setPointMarkerBorderWidth(1.0);
}
`
